import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { TuiAlertService } from '@taiga-ui/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';
import { ServicesService } from 'src/app/services/service/services.service';
import { GanttTask } from '../gantt/interfaces/Gant.interface';
import { Zone } from 'src/app/models/zone/zone.model';
import { Verticals } from 'src/app/models/service/service.model';
import { formatSecondsToHourAndMinutes, humanizeDuration } from 'src/app/utils';
import { TranslateService } from '@ngx-translate/core';
import { NodeStatusUpdateRequest } from 'src/app/models/service-manager/node-status.model';
import { finalize } from 'rxjs';
import { vehicleChasisTypeWithTranslations } from 'src/app/constants/data';

declare var MarkerClusterer: any;
declare var moment: any;
moment.locale('es');

export enum MapKind {
  Public = 'completePublicTransportModeMap',
  Driver = 'caflerDrivingModeMap',
}

@Component({
  selector: 'app-service-data-modal',
  templateUrl: './service-data-modal.component.html',
  styleUrls: ['./service-data-modal.component.css'],
})
export class ServiceDataModalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() bsModalRef?: BsModalRef;
  @Input() serviceData!: GanttTask;
  @Input() zoneId: string = '';
  @Input() zones!: Zone[];
  @Input() dateService!: Date | string;
  @Output() updateGantt = new EventEmitter<boolean>();

  @ViewChild('littleGmapCompleteContainer', { static: false })
  private gmapComplete?: ElementRef<HTMLDivElement>;

  @ViewChild('littleGmapContainer', { static: false })
  private gmapCafler?: ElementRef<HTMLDivElement>;

  isNodeLockedForDriverAssignation: boolean = true;

  public durationTotal: number = 0;
  public durationTransit: number = 0;
  public durationWalking: number = 0;

  public lat?: number;
  public lng?: number;
  public zoom_level: number = 6;

  public open: boolean = false;
  nodeStatusLoading: boolean = false;
  isMapLoading: boolean = false;

  public map!: google.maps.Map;

  customMarkers: google.maps.Marker[] = [];
  directionsRenderers: google.maps.DirectionsRenderer[] = [];
  public mapOptions!: google.maps.MapOptions;

  public directionsRenderer!: google.maps.DirectionsRenderer;

  markerCluster: any; // ER - I have to put any here forced
  public type_driver_start: string = 'service';
  public icon_driver_start: string = '';

  mapType: MapKind = MapKind.Driver;
  public mapKind = MapKind;

  modalInstance: BsModalRef | undefined;

  public verticals: Verticals[] = [];

  formatDuration = (sec: number): string =>
    formatSecondsToHourAndMinutes(String(sec));

  human = humanizeDuration;

  private get isPastRoute(): boolean {
    return this.routeDate.getTime() < Date.now() - 60_000;
  }

  routeDate!: Date;

  routeWarningKey: string | null = null;

  get currentLocale(): string {
    return this.translate.currentLang || 'es';
  }

  readonly dateFormat = "d 'de' MMMM 'de' y";

  constructor(
    private hs: HistoryServicesService,
    private router: Router,
    private servicesSvc: ServicesService,
    private readonly alerts: TuiAlertService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadVerticals();
    this.assignBlockedDriver();
    this.routeDate = this.buildRouteDate();
    this.updateRouteWarning();
  }

  private buildRouteDate(): Date {
    const base = new Date(this.dateService);
    const secs = Number(this.serviceData.serviceStartDate?.seconds ?? 0);
    return new Date(base.getTime() + secs * 1000);
  }

  assignRouteDate(): void {
    this.routeDate = new Date(
      Number(this.serviceData.serviceStartDate?.seconds ?? 0) * 1000
    );
    this.updateRouteWarning();
  }

  async ngAfterViewInit(): Promise<void> {
    this.mapInitializer();
  }

  loadVerticals(): void {
    this.verticals.push(...this.servicesSvc.verticals);
  }

  assignBlockedDriver(): void {
    this.isNodeLockedForDriverAssignation =
      this.serviceData.isNodeLockedForDriverAssignation;
  }

  private mapInitializer(): void {
    if (!this.serviceData) return;

    const container =
      this.mapType === 'completePublicTransportModeMap'
        ? this.gmapComplete?.nativeElement
        : this.gmapCafler?.nativeElement;

    if (!container) return;

    this.isMapLoading = true;

    const startLatLng = new google.maps.LatLng(
      this.serviceData.startLatitude,
      this.serviceData.startLongitude
    );

    const options: google.maps.MapOptions = {
      center: startLatLng,
      zoom: 6,
      disableDefaultUI: true,
      zoomControl: true,
    };

    this.destroyMapArtifacts();
    this.map = new google.maps.Map(container, options);
    this.map.addListener('idle', () => (this.isMapLoading = false));

    void this.drawRoutes();
  }

  private drawRoutes(): void {
    this.updateRouteWarning();

    const ds = new google.maps.DirectionsService();

    this.directionsRenderer = new google.maps.DirectionsRenderer({
      suppressMarkers: this.mapType === MapKind.Driver,
    });
    this.directionsRenderer.setMap(this.map);

    const { startLatitude, startLongitude, endLatitude, endLongitude } =
      this.serviceData;

    const travelMode =
      this.mapType === MapKind.Driver
        ? google.maps.TravelMode.DRIVING
        : google.maps.TravelMode.TRANSIT;

    const request: google.maps.DirectionsRequest = {
      origin: { lat: startLatitude, lng: startLongitude },
      destination: { lat: endLatitude, lng: endLongitude },
      travelMode,
      optimizeWaypoints: true,
      ...(travelMode === google.maps.TravelMode.DRIVING
        ? this.isPastRoute
          ? {}
          : {
              drivingOptions: {
                departureTime: this.routeDate,
                trafficModel: google.maps.TrafficModel.BEST_GUESS,
              },
            }
        : !(this.isPastRoute || this.tooFuture)
        ? { transitOptions: { departureTime: this.routeDate } }
        : {}),
    };

    setTimeout(() => (this.routeWarningKey = null));

    ds.route(request, (res, status) => {
      status === google.maps.DirectionsStatus.OK && res
        ? this.handleRouteSuccess(res)
        : this.handleRouteError(status);
    });
  }

  private get tooFuture(): boolean {
    return this.routeDate.getTime() - Date.now() > 7 * 24 * 60 * 60 * 1000;
  }

  getChassisLabel(code: string): string {
    const entry = vehicleChasisTypeWithTranslations.find(
      (c) => c.name.toLocaleUpperCase() === code.toLocaleUpperCase()
    );
    if (entry) {
      return this.translate.instant(`common.vehicleChassis.${entry.name}`);
    }
    return code;
  }

  private updateRouteWarning(): void {
    this.routeWarningKey = null;

    const tooFuture = this.tooFuture;
    const pastRoute = this.isPastRoute;

    if (this.mapType === MapKind.Public) {
      if (pastRoute) {
        this.routeWarningKey = 'service_manager.modal.past_transit_unavailable';
      } else if (tooFuture) {
        this.routeWarningKey =
          'service_manager.modal.future_transit_unavailable';
      }
    } else {
      if (pastRoute) {
        this.routeWarningKey = 'service_manager.modal.past_route_generic';
      }
    }
  }

  private handleRouteSuccess(result: google.maps.DirectionsResult): void {
    this.updateRouteWarning();
    this.directionsRenderer?.setDirections(result);
    this.calculateDurations(result);

    const bounds = result.routes[0].bounds;
    this.map.fitBounds(bounds, 64);

    if (this.mapType === MapKind.Driver) this.addCustomMarkers(result);
    this.updateRouteWarning();
  }

  private handleRouteError(status: google.maps.DirectionsStatus): void {
    this.isMapLoading = false;

    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    const tooFuture = this.routeDate.getTime() - Date.now() > sevenDaysMs;
    const pastRoute = this.routeDate.getTime() < Date.now() - 60_000;

    this.routeWarningKey = 'service_manager.modal.map_generic_error';

    /* ───────────────── CASOS ESPECIALES ───────────────── */

    /* 1. TRANSIT fuera de rango (pasado / >7 días futuro) → ZERO_RESULTS */
    if (
      status === google.maps.DirectionsStatus.ZERO_RESULTS &&
      this.mapType === MapKind.Public
    ) {
      this.routeWarningKey = pastRoute
        ? 'service_manager.modal.past_transit_unavailable'
        : tooFuture
        ? 'service_manager.modal.future_transit_unavailable'
        : 'service_manager.modal.no_route_found';

      return; // ⚠️  NO toast, solo badge amarillo
    }

    /* 2. Driver o Transit con ruta inexistente pero en rango */
    if (status === google.maps.DirectionsStatus.ZERO_RESULTS) {
      this.routeWarningKey = 'service_manager.modal.no_route_found';
      return; // sin toast
    }

    /* 3. Errores críticos que requieren atención del usuario */
    if (
      status === google.maps.DirectionsStatus.REQUEST_DENIED ||
      status === google.maps.DirectionsStatus.OVER_QUERY_LIMIT
    ) {
      this.routeWarningKey =
        status === google.maps.DirectionsStatus.REQUEST_DENIED
          ? 'service_manager.modal.request_denied'
          : 'service_manager.modal.over_query_limit';

      this.alerts
        .open(this.translate.instant(this.routeWarningKey), {
          status: 'error',
          autoClose: true,
        })
        .subscribe();
      return;
    }

    /* 4. INVALID_REQUEST u otros desconocidos → aviso genérico */
    this.routeWarningKey = 'service_manager.modal.map_generic_error';
    /* sin toast para no saturar al usuario */
  }

  private destroyMapArtifacts(): void {
    this.directionsRenderer?.setMap(null);

    for (const m of this.customMarkers) {
      m.setMap(null);
    }
    this.customMarkers = [];

    this.markerCluster?.clearMarkers?.();
    this.markerCluster = null!;
  }

  public getFormattedTime(seconds: string): string {
    return formatSecondsToHourAndMinutes(seconds);
  }

  onChangeMapType(newType: MapKind): void {
    if (this.mapType === newType) return;

    this.mapType = newType;
    this.updateRouteWarning();
    setTimeout(() => this.mapInitializer());
  }

  viewDetail(): void {
    this.closeModal();
    const datos = { service_info: this.serviceData };
    this.hs.setData(datos);

    const relativeUrl: UrlTree = this.router.createUrlTree(
      ['/service-manager/detail-service'],
      { queryParams: { orderHash: this.serviceData.orderHash } }
    );

    const absoluteUrl = `${window.location.origin}/#/${relativeUrl.toString()}`;

    window.open(absoluteUrl, '_blank');
  }

  closeModal(): void {
    this.bsModalRef?.hide();
  }

  getVertical(verticalType: string): string | undefined {
    return this.verticals.find((vertical) => vertical.type === verticalType)
      ?.name;
  }

  onToggle(locked: boolean) {
    this.isNodeLockedForDriverAssignation = locked;

    setTimeout(() => this.updateNodeStatus(), 0);
  }

  openInGoogleMaps(): void {
    const {
      startLatitude,
      startLongitude,
      endLatitude,
      endLongitude,
      providerUbication = [],
    } = this.serviceData;

    const origin = `${startLatitude},${startLongitude}`;
    const destination = `${endLatitude},${endLongitude}`;

    const waypoints = providerUbication
      .map((p) => `${p.providerLatitude},${p.providerLongitude}`)
      .join('|');

    const travelmode = this.mapType === MapKind.Driver ? 'driving' : 'transit';
    const departure = Math.floor(this.routeDate.getTime() / 1000);

    /* URL final ------------------------------------------------------ */
    const url =
      'https://www.google.com/maps/dir/?api=1' +
      `&origin=${encodeURIComponent(origin)}` +
      `&destination=${encodeURIComponent(destination)}` +
      `&travelmode=${travelmode}` +
      (this.isPastRoute ? '' : `&departure_time=${departure}`) +
      (waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : '');

    window.open(url, '_blank', 'noopener');
  }

  private calculateDurations(result: google.maps.DirectionsResult): void {
    this.durationTotal = this.durationTransit = this.durationWalking = 0;

    const add = (prop: 'durationTransit' | 'durationWalking', v: number) =>
      (this[prop] += v);

    result.routes[0].legs.forEach((leg) => {
      this.durationTotal += leg.duration?.value ?? 0;

      if (this.mapType === MapKind.Driver) return;

      leg.steps?.forEach((s) => {
        const v = s.duration?.value ?? 0;
        if (s.travel_mode === google.maps.TravelMode.TRANSIT)
          add('durationTransit', v);
        if (s.travel_mode === google.maps.TravelMode.WALKING)
          add('durationWalking', v);
      });
    });
  }

  private addCustomMarkers(response: google.maps.DirectionsResult): void {
    const route = response.routes[0];
    const legs = route?.legs;

    if (legs) {
      legs.forEach((leg: google.maps.DirectionsLeg, i: number) => {
        // Si existen waypoints, añade marcadores para cada uno
        if (leg.via_waypoints && leg.via_waypoints.length > 0) {
          leg.via_waypoints.forEach(
            (waypoint: google.maps.LatLng, index: number) => {
              const marker: google.maps.Marker = new google.maps.Marker({
                position: waypoint,
                map: this.map,
                icon: {
                  url: 'assets/img/icon_service3.png',
                  scaledSize: new google.maps.Size(30, 30),
                },
                title: '',
              });
              this.customMarkers.push(marker);
            }
          );
        }
        // Marcador de inicio (solo en el primer leg)
        if (i === 0) {
          const markerIcon: string =
            this.type_driver_start === 'service'
              ? 'assets/img/icon_service.png'
              : `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                  this.icon_driver_start
                )}`;
          const marker: google.maps.Marker = new google.maps.Marker({
            position: leg.start_location,
            map: this.map,
            icon: { url: markerIcon, scaledSize: new google.maps.Size(30, 30) },
            title:
              this.type_driver_start === 'service' ? leg.start_address : '',
          });
          this.customMarkers.push(marker);
        }
        // Marcador final o intermedio
        const marker: google.maps.Marker = new google.maps.Marker({
          position: leg.end_location,
          map: this.map,
          icon: {
            url:
              i === legs.length - 1
                ? 'assets/img/icon_service2.png'
                : 'assets/img/icon_service3.png',
            scaledSize: new google.maps.Size(30, 30),
          },
          title: leg.end_address,
        });
        this.customMarkers.push(marker);
      });
    }
    // Inicializa MarkerClusterer
    this.markerCluster = new MarkerClusterer(this.map, this.customMarkers, {
      styles: [
        { width: 30, height: 30, className: 'custom-clustericon-1' },
        { width: 30, height: 30, className: 'custom-clustericon-2' },
        { width: 30, height: 30, className: 'custom-clustericon-3' },
      ],
      clusterClass: 'custom-clustericon',
      zoomOnClick: false,
    });

    const infoWindow = new google.maps.InfoWindow({
      pixelOffset: new google.maps.Size(0, 0),
    });

    google.maps.event.addListener(
      this.markerCluster,
      'click',
      (cluster: any) => {
        const markers: google.maps.Marker[] = cluster.getMarkers();
        let content: string = `<table class="table table-bordered" style="width:700px"><tbody>`;

        markers.forEach((marker: google.maps.Marker) => {
          const icon = marker.getIcon();
          let iconUrl: string = '';

          if (typeof icon === 'string') {
            iconUrl = icon;
          } else if (icon && 'url' in icon && typeof icon.url === 'string') {
            iconUrl = icon.url;
          }

          if (marker.getTitle() !== '' && iconUrl) {
            content += `<tr><td><img src="${iconUrl}" width="30" /> ${marker.getTitle()}</td></tr>`;
          }
        });

        content += `</tbody></table>`;
        infoWindow.setContent(content);
        infoWindow.setPosition(cluster.getCenter());
        infoWindow.open(this.map);
      }
    );
  }

  updateNodeStatus(): void {
    if (!this.serviceData.orderHash || !this.serviceData.nodeId) {
      const msg = this.translate.instant(
        'service_manager.modal.node_locked_missing_data'
      );
      this.alerts.open(msg, { status: 'error', autoClose: true }).subscribe();
      return;
    }

    const payload: NodeStatusUpdateRequest = {
      orderHash: this.serviceData.orderHash,
      nodeId: this.serviceData.nodeId,
      desiredBlockState: this.isNodeLockedForDriverAssignation,
      isSweeperDriver: false
    };

    this.nodeStatusLoading = true;

    this.servicesSvc
      .updateNodeStatus(payload)
      .pipe(finalize(() => (this.nodeStatusLoading = false)))
      .subscribe({
        next: (response) => {
          if (response.wasSuccessful) {
            const key = this.isNodeLockedForDriverAssignation
              ? 'service_manager.modal.node_locked_success'
              : 'service_manager.modal.node_unlocked_success';

            this.alerts
              .open(this.translate.instant(key), {
                status: 'success',
                autoClose: true,
              })
              .subscribe();
            this.updateGantt.emit(true);
          } else {
            // Aquí ya no existe `err`, así que tiramos de mensaje genérico
            this.isNodeLockedForDriverAssignation = !payload.desiredBlockState;

            const errorKey = this.isNodeLockedForDriverAssignation
              ? 'service_manager.modal.node_unlocked_error'
              : 'service_manager.modal.node_locked_error';

            this.alerts
              .open(this.translate.instant(errorKey), {
                status: 'error',
                autoClose: true,
              })
              .subscribe();
          }
        },
        error: (err: any) => {
          this.isNodeLockedForDriverAssignation = !payload.desiredBlockState;

          const key = this.isNodeLockedForDriverAssignation
            ? 'service_manager.modal.node_unlocked_error'
            : 'service_manager.modal.node_locked_error';

          this.alerts
            .open(this.translate.instant(key, { error: err.message || err }), {
              status: 'error',
              autoClose: true,
            })
            .subscribe();
        },
      });
  }

  ngOnDestroy(): void {
    this.servicesSvc.ganttDataParams = {
      zoneId: this.zoneId,
      searchTerm: '',
      date: this.dateService,
    };
  }
}
